import apiClient from './apiClient'

export default {
  // Plan Catalog
  async searchProducts(params = {}) {
    return apiClient.post('/api/supplier/subscription/products/search', params).then(r => r.data)
  },
  async getProduct(id) {
    return apiClient.get(`/api/supplier/subscription/products/${id}`).then(r => r.data)
  },
  async createProduct(data) {
    return apiClient.post('/api/supplier/subscription/products', data).then(r => r.data)
  },
  async updateProduct(id, data) {
    return apiClient.put(`/api/supplier/subscription/products/${id}`, data).then(r => r.data)
  },
  async deleteProduct(id) {
    return apiClient.delete(`/api/supplier/subscription/products/${id}`).then(r => r.data)
  },
  async getPublicProducts() {
    return apiClient.get('/api/supplier/subscription/products/public').then(r => r.data)
  },

  // Product Prices
  async getProductPrices(productId) {
    return apiClient.get(`/api/supplier/subscription/products/${productId}/prices`).then(r => r.data)
  },
  async createProductPrice(productId, data) {
    return apiClient.post(`/api/supplier/subscription/products/${productId}/prices`, data).then(r => r.data)
  },
  async updateProductPrice(id, data) {
    return apiClient.put(`/api/supplier/subscription/prices/${id}`, data).then(r => r.data)
  },
  async deleteProductPrice(id) {
    return apiClient.delete(`/api/supplier/subscription/prices/${id}`).then(r => r.data)
  },

  // Tenant Subscriptions
  async searchTenantSubscriptions(params = {}) {
    return apiClient.post('/api/supplier/subscription/tenant/search', params).then(r => r.data)
  },
  async activateSubscription(data) {
    return apiClient.post('/api/supplier/subscription/tenant/activate', data).then(r => r.data)
  },
  // `immediately` cuts access off now; without it the customer keeps the period
  // they already paid for. `refund` returns the last payment in full.
  async cancelSubscription(id, options = {}) {
    return apiClient
      .put(`/api/supplier/subscription/tenant/${id}/cancel`, {
        immediately: options.immediately ?? false,
        refund: options.refund ?? false
      })
      .then(r => r.data)
  },
  async getCurrentStatus() {
    return apiClient.get('/api/supplier/subscription/tenant/current-status').then(r => r.data)
  },
  async getTenantStatus(tenantId) {
    return apiClient.get(`/api/supplier/subscription/tenant/${tenantId}/status`).then(r => r.data)
  },

  // Discount Codes
  async getDiscountCode(id) {
    return apiClient.get(`/api/supplier/subscription/discount-codes/${id}`).then(r => r.data)
  },
  async searchDiscountCodes(params = {}) {
    return apiClient.post('/api/supplier/subscription/discount-codes/search', params).then(r => r.data)
  },
  async createDiscountCode(data) {
    return apiClient.post('/api/supplier/subscription/discount-codes', data).then(r => r.data)
  },
  async updateDiscountCode(id, data) {
    return apiClient.put(`/api/supplier/subscription/discount-codes/${id}`, data).then(r => r.data)
  },
  async deleteDiscountCode(id) {
    return apiClient.delete(`/api/supplier/subscription/discount-codes/${id}`).then(r => r.data)
  },
  async generateDiscountCode(data) {
    return apiClient.post('/api/supplier/subscription/discount-codes/generate', data).then(r => r.data)
  },
  async validateDiscountCode(data) {
    return apiClient.post('/api/supplier/subscription/discount-codes/validate', data).then(r => r.data)
  },

  /**
   * Tenants an admin can target with a discount code, newest first.
   *
   * Served by the subscription module rather than the super-admin tenant
   * endpoint: that one needs the TenantSetup permission, while this screen needs
   * Subscriptions.Access, so an admin allowed to run campaigns could not
   * otherwise fill in the picker.
   */
  async getDiscountTenantOptions() {
    return apiClient.get('/api/supplier/subscription/discount-codes/tenant-options').then(r => r.data)
  },

  // Self-activation (tenant activates their own subscription)
  async selfActivate(data) {
    return apiClient.post('/api/supplier/subscription/self/activate', data).then(r => r.data)
  },

  // Self-cancel (tenant cancels at period end)
  async selfCancel() {
    return apiClient.put('/api/supplier/subscription/self/cancel').then(r => r.data)
  },

  // Self-resume (tenant undoes a pending cancellation before period end)
  async selfResume() {
    return apiClient.put('/api/supplier/subscription/self/resume').then(r => r.data)
  },

  // Self-switch (schedules a plan change that starts when the current period ends)
  async selfSwitch(data) {
    return apiClient.post('/api/supplier/subscription/self/switch', data).then(r => r.data)
  },

  /**
   * The logged-in tenant's own billing history.
   *
   * Scoped server-side to the caller's tenant, so there is no tenant parameter to
   * pass or to get wrong. Reads our local mirror of Stripe invoices rather than
   * calling Stripe, which keeps the list available even when Stripe is
   * unreachable; each row carries Stripe's own PDF and hosted-page links for the
   * authoritative document.
   */
  async getMyInvoices(params = {}) {
    return apiClient.post('/api/supplier/stripe/invoices/search', params).then(r => r.data)
  },

  // Stripe
  //
  // Pushes every plan and price that has no Stripe id yet. Safe to re-run: rows
  // already synced are skipped, so this doubles as the recovery path after a
  // Stripe outage (a failed sync never blocks the local save).
  async syncStripeCatalog() {
    return apiClient.post('/api/supplier/stripe/sync-catalog').then(r => r.data)
  },

  // The opposite direction: creates local plans and prices from what already
  // exists in Stripe, so a catalog authored in the dashboard — or one whose local
  // rows were wiped — does not have to be retyped.
  async importStripeCatalog() {
    return apiClient.post('/api/supplier/stripe/import-catalog').then(r => r.data)
  },

  // Starts a Stripe checkout. Returns the PaymentIntent client secret the browser
  // confirms with Stripe.js, plus the publishable key so the frontend does not
  // have to embed it at build time (test and live share one build).
  //
  // The subscription is NOT active when this returns: it becomes active only when
  // Stripe reports invoice.paid to our webhook.
  async createStripeCheckout(data) {
    return apiClient.post('/api/supplier/stripe/checkout', data).then(r => r.data)
  },

  // Pulls current state from Stripe instead of waiting for a webhook. Used after
  // a confirmed payment so the UI does not sit on a stale "incomplete" status,
  // and as the fallback where webhooks cannot reach the deployment at all.
  async reconcileSelf() {
    return apiClient.post('/api/supplier/stripe/reconcile/self').then(r => r.data)
  }
}
